// src/pages/BasicDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import FloatingNotice from "../components/FloatingNotice";
import ReferralWithdrawalCard from "../components/ReferralWithdrawalCard";
import { useCountry } from "../context/CountryContext";

import opayLogo from "../assets/opay.png";
import palmpayLogo from "../assets/palmpay.png";
import kudaLogo from "../assets/kuda.png";
import moniepointLogo from "../assets/moniepoint.png";
import binanceLogo from "../assets/binance.png";
import bybitLogo from "../assets/bybit.png";
import lunoLogo from "../assets/luno.png";
import paypalLogo from "../assets/paypal.png";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";
const TAWK_SRC = "https://embed.tawk.to/68deec7532ab2019572628de/1j6jcnbu7";

const TelegramBotBanner = ({ onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-sky-500/15 via-sky-500/5 to-transparent border border-sky-400/25 flex items-center justify-between gap-3 flex-wrap backdrop-blur-sm"
  >
    <div className="flex items-start gap-3">
      <span className="text-2xl">🤖</span>
      <div>
        <p className="font-semibold text-white text-sm">Need help? Chat with our Telegram assistant</p>
        <p className="text-xs text-slate-400 mt-0.5">
          Register, check your balance, buy coins, and get answers instantly.
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-400 text-black transition shadow-lg shadow-sky-500/20 whitespace-nowrap"
      >
        Open Telegram Bot
      </a>
      <button onClick={onDismiss} className="text-slate-500 hover:text-white text-lg leading-none px-1" aria-label="Dismiss">✕</button>
    </div>
  </motion.div>
);

const TawkFloatingButton = () => {
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    if (document.getElementById("tawk-basic-script")) return;
    const s1 = document.createElement("script");
    s1.id = "tawk-basic-script";
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");
    s1.onload = () => {
      if (window.Tawk_API?.hideWidget) window.Tawk_API.hideWidget();
    };
    document.body.appendChild(s1);
  }, []);

  const openTawkChat = () => {
    if (!window.Tawk_API || typeof window.Tawk_API.maximize !== "function") {
      setStatusMsg("Support chat isn't ready yet — try again in a moment.");
      setTimeout(() => setStatusMsg(""), 4000);
      return;
    }
    window.Tawk_API.showWidget();
    window.Tawk_API.maximize();
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {statusMsg && (
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg whitespace-nowrap shadow-lg max-w-xs">
          {statusMsg}
        </div>
      )}
      <button
        onClick={openTawkChat}
        className="px-5 py-3 bg-gradient-to-r from-sky-400 to-sky-500 hover:from-sky-300 hover:to-sky-400 text-black font-semibold rounded-xl shadow-2xl shadow-sky-500/30 transition active:scale-95"
      >
        Talk to Support
      </button>
    </div>
  );
};

export default function BasicDashboard() {
  const { isGhana, config } = useCountry();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [telegramBannerVisible, setTelegramBannerVisible] = useState(true);
  // ✅ NEW: surfaces a real fetch failure instead of hanging on
  // "LOADING DASHBOARD" forever with no visible cause.
  const [loadError, setLoadError] = useState("");

  const currencySymbol = config.currencySymbol;
  const flag = isGhana ? "🇬🇭" : "🇳🇬";

  const wallets = isGhana
    ? [
        { name: "MTN MoMo", emoji: "📱", link: "/wallet", active: true },
        { name: "Telecel Cash", emoji: "💳", link: "/wallet-palmpay", active: true },
        { name: "AirtelTigo", emoji: "💰", link: "/wallet-kuda", active: true },
        { name: "GCB Bank", emoji: "🏦", link: "/moniepoint", active: true },
      ]
    : [
        { name: "Opay", logo: opayLogo, link: "/wallet", active: true },
        { name: "PalmPay", logo: palmpayLogo, link: "/wallet-palmpay", active: true },
        { name: "Kuda", logo: kudaLogo, link: "/wallet-kuda", active: true },
        { name: "MoniePoint", logo: moniepointLogo, link: "/moniepoint", active: true },
      ];

  const cryptoWallets = [
    { name: "Binance", logo: binanceLogo, active: false },
    { name: "Bybit", logo: bybitLogo, active: false },
    { name: "Luno", logo: lunoLogo, active: true, link: "/luno-dashboard" },
    { name: "PayPal", logo: paypalLogo, active: false },
  ];

  const fetchDashboard = async () => {
    try {
      const res = await API.get("/dashboard");
      setData(res.data);
      setLoadError("");
      if (res.data.user) localStorage.setItem("user", JSON.stringify(res.data.user));
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setLoadError(
        `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://prorealalert.com/register?ref=${data.user?.referralCode || ""}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showComingSoon = (name) => {
    setNotice(`${name} is coming soon!`);
    setTimeout(() => setNotice(""), 2000);
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
        <div className="text-center max-w-md">
          <p className="text-slate-400 text-sm tracking-wide animate-pulse mb-4">LOADING DASHBOARD</p>
          {loadError && (
            <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs text-left break-words">
              🔬 Debug: {loadError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (data.user?.accountLocked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950 text-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-400">Account Locked</h2>
          <p className="mt-2 text-slate-400">{data.user.accountLockReason}</p>
        </div>
      </div>
    );
  }

  const bankWallets = wallets.map((w) => ({ ...w, balance: data.user?.balance || 0 }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-[#0a1122] text-white px-5 py-8 md:px-10 md:py-10 relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-sky-500/10 blur-[120px]" />
      <div className="pointer-events-none fixed -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-indigo-500/10 blur-[120px]" />

      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700/50">
        <span className="text-sm leading-none">{flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <div className="max-w-6xl mx-auto relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h1 className="text-3xl font-bold">
            Hello, <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-cyan-300">{data.user?.name || "User"}</span>
          </h1>
          <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-sky-500/20 to-cyan-500/20 text-sky-300 border border-sky-400/40 rounded-full shadow-lg shadow-sky-500/10">
            ✦ Basic Plan
          </span>
        </div>
        <p className="text-sm text-slate-400 mb-6">Real-time alerts and transaction reflection.</p>

        {telegramBannerVisible && <TelegramBotBanner onDismiss={() => setTelegramBannerVisible(false)} />}

        {data.user?.isRestricted && (
          <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm">
            ⚠️ {data.user.customMessage || "Your account is limited"}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl p-8 mb-6 overflow-hidden bg-gradient-to-br from-sky-600/20 via-slate-900/80 to-indigo-900/30 border border-sky-500/20 shadow-2xl shadow-sky-950/50"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-sky-400/10 rounded-full blur-3xl -mr-20 -mt-20" />
          <div className="relative">
            <p className="text-xs uppercase tracking-widest text-sky-300/70 mb-2 font-semibold">Available Balance</p>
            <p className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-white via-sky-100 to-sky-300 mb-3">
              {currencySymbol}{data.user?.balance?.toLocaleString() || "0"}
            </p>
            <p className="text-sm text-slate-400">
              Withdrawable: <span className="text-slate-200 font-medium">{currencySymbol}{data.user?.withdrawableBalance?.toLocaleString() || 0}</span>
            </p>

            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={() => navigate("/purchase")}
                className="px-5 py-2.5 rounded-xl font-semibold bg-gradient-to-r from-sky-400 to-cyan-400 hover:from-sky-300 hover:to-cyan-300 text-black transition shadow-lg shadow-sky-500/30"
              >
                + Add Money
              </button>
              <button
                disabled={!data.user?.canWithdraw}
                onClick={() => navigate("/withdraw")}
                className="px-5 py-2.5 rounded-xl font-semibold bg-white/10 hover:bg-white/15 border border-white/10 disabled:opacity-40 transition backdrop-blur-sm"
              >
                Withdraw
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mb-6">
          <div className="md:col-span-2 p-6 rounded-2xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 backdrop-blur-sm shadow-xl">
            <h3 className="font-semibold text-lg mb-1">Wallets</h3>
            <p className="text-xs text-slate-500 mb-5">Access your bank and crypto wallets</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {bankWallets.map((wallet, i) => (
                <button
                  key={i}
                  onClick={() => (wallet.active && wallet.link ? navigate(wallet.link) : showComingSoon(wallet.name))}
                  className="p-4 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-slate-700/50 hover:border-sky-500/30 flex flex-col items-center transition shadow-lg"
                >
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center overflow-hidden mb-2 shadow-inner">
                    {wallet.logo ? <img src={wallet.logo} alt={wallet.name} className="w-full h-full object-contain" /> : <span className="text-lg">{wallet.emoji}</span>}
                  </div>
                  <p className="text-xs font-medium text-slate-200">{wallet.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{currencySymbol}{(wallet.balance || 0).toLocaleString()}</p>
                </button>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-700/50">
              <p className="text-xs font-semibold text-slate-400 mb-3">Crypto Wallets</p>
              <div className="flex flex-wrap gap-2">
                {cryptoWallets.map((wallet, i) => (
                  <button
                    key={i}
                    onClick={() => (wallet.active && wallet.link ? navigate(wallet.link) : showComingSoon(wallet.name))}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 hover:bg-slate-800/80 border border-slate-700/50 transition"
                  >
                    <img src={wallet.logo} alt={wallet.name} className="w-5 h-5 object-contain" />
                    <span className="text-xs text-slate-300">{wallet.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar column — Referrals + Withdrawal + Notifications */}
          <div className="space-y-5">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-slate-900/60 border border-emerald-500/20 backdrop-blur-sm shadow-xl">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Referrals</h3>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Count</p>
                  <p className="font-bold text-white text-lg">{data.user?.referralCount || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Earned</p>
                  <p className="font-bold text-emerald-400 text-lg">{currencySymbol}{data.user?.referralEarnings || 0}</p>
                </div>
              </div>
              <button
                onClick={handleCopy}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-black font-semibold text-sm transition shadow-lg shadow-emerald-500/20"
              >
                {copied ? "✓ Copied!" : "Copy Referral Link"}
              </button>
            </div>

            {/* ✅ NEW: was completely missing on Basic — users had no way
                to see eligible-for-withdrawal count or request a payout. */}
            <ReferralWithdrawalCard
              currencySymbol={currencySymbol}
              theme={{
                card: "bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 backdrop-blur-sm shadow-xl",
                accent: "bg-gradient-to-r from-sky-400 to-cyan-400 hover:from-sky-300 hover:to-cyan-300 text-black",
              }}
            />

            <div className="p-5 rounded-2xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 backdrop-blur-sm shadow-xl">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">Notifications</h3>
              {data.user?.notifications?.length > 0 ? (
                <div className="space-y-2">
                  {data.user.notifications.slice(0, 3).map((n, i) => (
                    <p key={i} className="text-xs text-slate-400">• {n.message}</p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500">No notifications yet</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 backdrop-blur-sm shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-300">Latest Transactions</h3>
            <span className="text-xs text-sky-400 cursor-pointer hover:underline">See all</span>
          </div>
          <div className="space-y-2.5">
            {(data.transactions || []).slice(0, 4).map((tx, i) => (
              <div key={i} className="flex items-center justify-between text-sm p-2.5 rounded-lg hover:bg-white/[0.03] transition">
                <span className="text-slate-400 truncate">{tx.description || "Transaction"}</span>
                <span className={`font-medium ${tx.amount > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                </span>
              </div>
            ))}
            {(!data.transactions || data.transactions.length === 0) && (
              <p className="text-xs text-slate-500">No recent activity</p>
            )}
          </div>
        </div>
      </div>

      {notice && <FloatingNotice message={notice} />}
      <TawkFloatingButton />
    </div>
  );
            }
